<template>
  <div
    ref="profileModal"
    class="uk-modal-full instructor-profile-modal"
    uk-modal
  >
    <div class="uk-modal-footer uk-text-right instructor-profile-modal__footer">
      <button class="uk-button uk-button-default small" type="button" @click="closeModal">
        <i class="uil-times"></i> {{ $t("general.close") }}
      </button>
    </div>

    <div class="uk-modal-dialog instructor-profile-modal__dialog">
      <default-loading v-if="loading" min-height="70vh"></default-loading>

      <div v-else class="instructor-profile-layout">
        <aside class="instructor-profile-sidebar">
          <div class="instructor-profile-card">
            <div class="instructor-profile-card__cover"></div>
            <img
              :src="instructor.photo"
              :alt="instructor.name"
              class="instructor-profile-card__photo"
              @error="useFallbackPhoto"
            >
            <div class="instructor-profile-card__body">
              <h2>{{ instructor.name }}</h2>
              <p class="instructor-profile-card__title">
                {{ instructor.title || $t("general.title_not_specified") }}
              </p>
              <span class="instructor-profile-card__type">{{ typeLabel }}</span>

              <dl class="instructor-profile-card__meta">
                <div>
                  <dt>{{ $t("general.status") }}</dt>
                  <dd :class="`status-${statusClass}`">{{ statusLabel }}</dd>
                </div>
                <div>
                  <dt>{{ $t("general.department") }}</dt>
                  <dd>{{ instructor.department || emptyValue }}</dd>
                </div>
                <div>
                  <dt>{{ $t("general.institution") }}</dt>
                  <dd>{{ instructor.institutionName || emptyValue }}</dd>
                </div>
                <div>
                  <dt>{{ $t("general.created_at") }}</dt>
                  <dd>{{ formattedCreatedAt }}</dd>
                </div>
              </dl>

              <button
                v-if="$hasPermission('update_instructor')"
                class="btn btn-default instructor-profile-card__edit"
                type="button"
                @click="$emit('edit', instructor)"
              >
                <i class="uil-edit"></i> {{ $t("general.edit") }}
              </button>
            </div>
          </div>
        </aside>

        <main class="instructor-profile-content">
          <div class="instructor-profile-stats">
            <div class="instructor-profile-stat">
              <span class="instructor-profile-stat__icon"><i class="uil-clipboard-alt"></i></span>
              <div>
                <strong>{{ assignmentsCount }}</strong>
                <span>{{ $t("general.assignments") }}</span>
              </div>
            </div>
            <div class="instructor-profile-stat">
              <span class="instructor-profile-stat__icon"><i class="uil-book-open"></i></span>
              <div>
                <strong>{{ contentsCount }}</strong>
                <span>{{ $t("general.contents") }}</span>
              </div>
            </div>
            <div class="instructor-profile-stat">
              <span class="instructor-profile-stat__icon"><i class="uil-award"></i></span>
              <div>
                <strong>{{ instructor.experienceYears || emptyValue }}</strong>
                <span>{{ $t("general.experience") }}</span>
              </div>
            </div>
          </div>

          <section class="instructor-profile-section instructor-profile-section--about">
            <div class="instructor-profile-section__heading">
              <i class="uil-user-square"></i>
              <h3>{{ $t("general.about") }}</h3>
            </div>
            <p>
              {{ instructor.bio || $t("general.instructor_description_not_found") }}
            </p>
          </section>

          <div class="instructor-profile-grid">
            <section class="instructor-profile-section">
              <div class="instructor-profile-section__heading">
                <i class="uil-medal"></i>
                <h3>{{ $t("general.expertise_areas") }}</h3>
              </div>
              <div v-if="instructor.expertiseAreas.length" class="instructor-profile-tags">
                <span v-for="expertise in instructor.expertiseAreas" :key="expertise">
                  {{ expertise }}
                </span>
              </div>
              <p v-else class="instructor-profile-empty">
                {{ $t("general.expertise_area_not_specified") }}
              </p>
            </section>

            <section class="instructor-profile-section">
              <div class="instructor-profile-section__heading">
                <i class="uil-award"></i>
                <h3>{{ $t("general.educations_and_certificates") }}</h3>
              </div>
              <ul v-if="instructor.educationsCertificates.length" class="instructor-profile-certificates">
                <li
                  v-for="(certificate, index) in instructor.educationsCertificates"
                  :key="`certificate-${index}`"
                >
                  <i class="uil-check-circle"></i>
                  <span>{{ certificate }}</span>
                </li>
              </ul>
              <p v-else class="instructor-profile-empty">{{ emptyValue }}</p>
            </section>
          </div>

          <section class="instructor-profile-section">
            <div class="instructor-profile-section__heading">
              <i class="uil-user"></i>
              <h3>{{ $t("general.instructor_detail") }}</h3>
            </div>
            <div class="instructor-profile-information">
              <div v-if="!shouldHideEmailField">
                <span>{{ $t("general.email") }}</span>
                <strong>{{ email || emptyValue }}</strong>
              </div>
              <div>
                <span>{{ $t("users.mission") }}</span>
                <strong>{{ position || emptyValue }}</strong>
              </div>
              <div>
                <span>{{ $t("general.instructor_type") }}</span>
                <strong>{{ typeLabel }}</strong>
              </div>
              <div v-if="instructor.linkedinUrl">
                <span>{{ $t("general.linkedin_profile") }}</span>
                <a :href="instructor.linkedinUrl" target="_blank" rel="noopener noreferrer">
                  {{ instructor.linkedinUrl }}
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import {
  INSTRUCTOR_STATUSES,
  INSTRUCTOR_TYPES,
} from "@/core/services/instructor.service";

const FALLBACK_PHOTO = "https://cdn.anzera.com.tr/softinya/not-found.png";

export default {
  name: "InstructorProfile",
  components: {
    DefaultLoading,
  },
  props: {
    instructor: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalInstance: null,
      emptyValue: "—",
    };
  },
  computed: {
    rawInstructor() {
      return this.instructor.raw || {};
    },
    rawUser() {
      return this.instructor.user?.raw || this.rawInstructor.user || {};
    },
    assignmentsCount() {
      return Number(this.rawInstructor.assignments_count) || 0;
    },
    contentsCount() {
      return Number(this.rawInstructor.contents_count) || 0;
    },
    email() {
      return this.instructor.user?.email
        || this.rawUser.email
        || this.rawUser.mail
        || "";
    },
    shouldHideEmailField() {
      const companyId = this.rawUser.company_id
        || this.rawUser.company?.id
        || this.rawInstructor.company_id;
      return Number(companyId) === 489;
    },
    position() {
      return this.instructor.user?.position
        || this.rawUser.position_name
        || this.instructor.title
        || "";
    },
    typeLabel() {
      return this.instructor.type === INSTRUCTOR_TYPES.EXTERNAL
        ? this.$t("general.external_instructor")
        : this.$t("general.internal_instructor");
    },
    statusLabel() {
      return Number(this.instructor.statusId) === INSTRUCTOR_STATUSES.PASSIVE
        ? this.$t("general.passive")
        : this.$t("general.active");
    },
    statusClass() {
      return Number(this.instructor.statusId) === INSTRUCTOR_STATUSES.PASSIVE
        ? "passive"
        : "active";
    },
    formattedCreatedAt() {
      if (!this.instructor.createdAt) return this.emptyValue;
      return new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(this.instructor.createdAt));
    },
  },
  mounted() {
    this.$nextTick(() => {
      const uiKit = window.UIkit;
      if (!uiKit || !this.$refs.profileModal) return;

      this.modalInstance = uiKit.modal(this.$refs.profileModal);
      uiKit.util.on(this.$refs.profileModal, "hidden", this.handleHidden);
      this.modalInstance.show();
    });
  },
  beforeDestroy() {
    const uiKit = window.UIkit;
    if (uiKit && this.$refs.profileModal) {
      uiKit.util.off(this.$refs.profileModal, "hidden", this.handleHidden);
    }
    if (this.modalInstance) {
      this.modalInstance.$destroy(true);
      this.modalInstance = null;
    }
  },
  methods: {
    closeModal() {
      if (this.modalInstance) this.modalInstance.hide();
    },
    handleHidden() {
      this.$emit("modalClosed");
    },
    useFallbackPhoto(event) {
      if (event.target.src !== FALLBACK_PHOTO) {
        event.target.src = FALLBACK_PHOTO;
      }
    },
  },
};
</script>

<style scoped>
.instructor-profile-modal__footer {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 12px 24px;
  border-bottom: 1px solid #e4e8ef;
  background: #fff;
}

.instructor-profile-modal__dialog {
  min-height: 100%;
  padding: 28px;
  background: #edeff3;
}

.instructor-profile-layout {
  display: grid;
  grid-template-columns: minmax(240px, 300px) minmax(0, 1fr);
  gap: 20px;
  width: min(1260px, 100%);
  margin: 0 auto;
}

.instructor-profile-card,
.instructor-profile-section,
.instructor-profile-stat {
  border: 1px solid #e1e6ee;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 3px 12px rgba(26, 39, 67, 0.06);
}

.instructor-profile-card {
  position: sticky;
  top: 78px;
  overflow: hidden;
}

.instructor-profile-card__cover {
  height: 92px;
  background: linear-gradient(135deg, var(--primaryColor), #6f5bd3);
}

.instructor-profile-card__photo {
  display: block;
  width: 122px;
  height: 122px;
  margin: -61px auto 0;
  border: 5px solid #fff;
  border-radius: 50%;
  object-fit: cover;
  object-position: center top;
  background: #eef1f5;
}

.instructor-profile-card__body {
  padding: 18px 22px 22px;
  text-align: center;
}

.instructor-profile-card__body h2 {
  margin: 0;
  color: #17213a;
  font-size: 22px;
}

.instructor-profile-card__title {
  margin: 5px 0 10px;
  color: #6c778b;
  font-size: 13px;
}

.instructor-profile-card__type {
  display: inline-flex;
  padding: 5px 10px;
  border-radius: 999px;
  color: var(--primaryColor);
  background: #eef3ff;
  font-size: 11px;
  font-weight: 700;
}

.instructor-profile-card__meta {
  margin: 20px 0;
  text-align: left;
}

.instructor-profile-card__meta > div {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 0;
  border-bottom: 1px solid #edf0f4;
}

.instructor-profile-card__meta dt,
.instructor-profile-card__meta dd {
  margin: 0;
  font-size: 12px;
}

.instructor-profile-card__meta dt {
  color: #8490a3;
}

.instructor-profile-card__meta dd {
  overflow-wrap: anywhere;
  color: #354056;
  font-weight: 600;
  text-align: right;
}

.instructor-profile-card__meta .status-active {
  color: #2e9e65;
}

.instructor-profile-card__meta .status-passive {
  color: #d74c4c;
}

.instructor-profile-card__edit {
  width: 100%;
}

.instructor-profile-content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 18px;
}

.instructor-profile-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.instructor-profile-stat {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
}

.instructor-profile-stat__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 11px;
  color: var(--primaryColor);
  background: #eef3ff;
  font-size: 20px;
}

.instructor-profile-stat div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.instructor-profile-stat strong {
  color: #17213a;
  font-size: 21px;
}

.instructor-profile-stat div span {
  color: #8490a3;
  font-size: 11px;
}

.instructor-profile-section {
  padding: 22px;
}

.instructor-profile-section__heading {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 15px;
}

.instructor-profile-section__heading i {
  color: var(--primaryColor);
  font-size: 19px;
}

.instructor-profile-section__heading h3 {
  margin: 0;
  color: #253047;
  font-size: 16px;
}

.instructor-profile-section--about p,
.instructor-profile-empty {
  margin: 0;
  color: #68758a;
  font-size: 13px;
  line-height: 1.7;
}

.instructor-profile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.instructor-profile-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.instructor-profile-tags span {
  padding: 6px 10px;
  border-radius: 999px;
  color: var(--primaryColor);
  background: #eef3ff;
  font-size: 11px;
  font-weight: 600;
}

.instructor-profile-certificates {
  display: grid;
  gap: 9px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.instructor-profile-certificates li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #667085;
  font-size: 12px;
}

.instructor-profile-certificates i {
  color: #2e9e65;
  font-size: 15px;
}

.instructor-profile-information {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.instructor-profile-information > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
  padding: 13px 14px;
  border-radius: 10px;
  background: #f7f9fc;
}

.instructor-profile-information span {
  color: #8a95a7;
  font-size: 10px;
}

.instructor-profile-information strong,
.instructor-profile-information a {
  overflow-wrap: anywhere;
  color: #3d485d;
  font-size: 12px;
  font-weight: 600;
}

.instructor-profile-information a {
  color: var(--primaryColor);
}

@media (max-width: 900px) {
  .instructor-profile-modal__dialog {
    padding: 18px;
  }

  .instructor-profile-layout {
    grid-template-columns: 1fr;
  }

  .instructor-profile-card {
    position: static;
  }
}

@media (max-width: 640px) {
  .instructor-profile-modal__footer {
    padding: 10px 14px;
  }

  .instructor-profile-modal__footer button {
    width: 100%;
  }

  .instructor-profile-modal__dialog {
    padding: 12px;
  }

  .instructor-profile-stats,
  .instructor-profile-grid,
  .instructor-profile-information {
    grid-template-columns: 1fr;
  }
}
</style>
